import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { useCancelBookingDialog } from "../useCancelBookingDialog";

export const ConfirmScreen = () => {
  const { isRecurring, fromDate, willBeCharged, canWaive, waive, setWaive, waiveNote, setWaiveNote, isSubmitting, error, submit, goBack } = useCancelBookingDialog();
  const [confirmed, setConfirmed] = useState(false);
  // When waiving, a reason is MANDATORY — mirrors the server, which rejects an empty note. Gate on
  // `canWaive` (elevated + chargeable) so a `waive` left true from an earlier chargeable from-date
  // can't block submit once the waive UI (and its note field) is no longer shown.
  const waiveNoteMissing = canWaive && waive && !waiveNote.trim();

  return (
    <div data-testid="cancel-dialog-confirm" className="w-full flex flex-col gap-4 bg-white rounded-xl my-4 p-6">
      <h2 className="text-2xl font-semibold">Confirm cancellation</h2>
      <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
        {isRecurring && fromDate
          ? <p>All occurrences from <span className="font-semibold">{fromDate.toDateString()}</span> onward will be cancelled.</p>
          : <p>This booking will be cancelled.</p>}
        {willBeCharged && <p className="mt-2 text-amber-700 font-medium">This cancellation is within 24 hours and will be charged{waive ? " — charge will be waived" : ""}.</p>}
      </div>
      {canWaive && (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox data-testid="cancel-waive-checkbox" checked={waive} onCheckedChange={(v) => setWaive(Boolean(v))} />
          <span>Waive this charge (the member will not be charged).</span>
        </label>
      )}
      {canWaive && waive && (
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="cancel-waive-note" className="font-medium">
            Reason for waiving <span className="text-red-600">*</span>
          </label>
          <Textarea
            id="cancel-waive-note"
            data-testid="cancel-waive-note"
            value={waiveNote}
            onChange={(e) => setWaiveNote(e.target.value)}
            placeholder="Explain why this charge is being waived (required)"
            rows={3}
          />
          {waiveNoteMissing && <span className="text-xs text-red-600">A reason is required to waive the charge.</span>}
        </div>
      )}
      <label className="flex items-start gap-2 text-sm">
        <Checkbox data-testid="cancel-confirm-checkbox" checked={confirmed} onCheckedChange={(v) => setConfirmed(Boolean(v))} />
        <span>I understand this cannot be undone and want to cancel{willBeCharged && !waive ? ", accepting the charge" : ""}.</span>
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-col gap-2 mt-2">
        <Button data-testid="cancel-confirm-submit" variant="destructive" className={`w-full ${defaultButtonClasses}`} disabled={!confirmed || isSubmitting || waiveNoteMissing} onClick={submit}>
          {isSubmitting ? "Cancelling…" : isRecurring ? "Cancel occurrences" : "Confirm cancellation"}
        </Button>
        <Button variant="outline" className={`w-full ${secondaryButtonClasses}`} disabled={isSubmitting} onClick={goBack}>Back</Button>
      </div>
    </div>
  );
};
